// @file Fetch initial state for the surface page.
import { captureFetchException } from '@@/bits/error_tracker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import PadletApi from '@@/surface/padlet_api'
import { storeToRefs } from 'pinia'
import { onBeforeMount } from 'vue'

interface InitialStateFetchOptions {
  postDrafts?: boolean
  wallPostProperties?: boolean
  wallCustomPostProperties?: boolean
  submissionRequestLink?: boolean
}

export const useSurfaceFetchInitialState = ({
  postDrafts = true,
  wallPostProperties = true,
  wallCustomPostProperties = true,
  submissionRequestLink = true,
}: InitialStateFetchOptions = {}): void => {
  const { wallId } = storeToRefs(useSurfaceStore())
  const { amIRegistered } = storeToRefs(useSurfacePermissionsStore())
  const surfacePostDraftsStore = useSurfaceDraftsStore()
  const { hasFetchedInitialPostDrafts } = storeToRefs(surfacePostDraftsStore)

  const { fetchPostDrafts } = surfacePostDraftsStore
  const { fetchSubmissionRequestLink } = useSurfaceShareLinksStore()
  const { setWallPostProperties, setCustomPostProperties } = useSurfacePostPropertiesStore()

  /** Define */
  const fetchWallPostPropertiesAndSaveToStore = async (): Promise<void> => {
    try {
      const postProperties = await PadletApi.WallPostProperties.fetchWallPostProperties(wallId.value)
      setWallPostProperties(postProperties)
    } catch (e) {
      captureFetchException(e, { source: 'useSurfaceFetchInitialState' })
    }
  }

  const fetchWallCustomPostPropertiesAndSaveToStore = async (): Promise<void> => {
    try {
      const customPostProperties = await PadletApi.WallCustomPostProperties.fetchWallCustomPostProperties(wallId.value)
      setCustomPostProperties(customPostProperties)
    } catch (e) {
      captureFetchException(e, { source: 'useSurfaceFetchInitialState' })
    }
  }

  onBeforeMount(() => {
    /** Register */
    const promises: Array<Promise<void>> = []
    if (postDrafts && amIRegistered.value) {
      promises.push(fetchPostDrafts())
    } else {
      // There are some places that rely on this state to be `true` (e.g. `SurfaceComposerModalList`).
      // To simplify things, we always populate it even if the user can't create drafts (hence we don't
      // fetch drafts for them).
      hasFetchedInitialPostDrafts.value = true
    }

    if (wallPostProperties) {
      promises.push(fetchWallPostPropertiesAndSaveToStore())
    }

    if (wallCustomPostProperties) {
      promises.push(fetchWallCustomPostPropertiesAndSaveToStore())
    }

    if (submissionRequestLink) promises.push(fetchSubmissionRequestLink())

    /** Execute */
    void Promise.all(promises)
  })
}
